import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { HowToReg } from "@mui/icons-material";
import { useEffect, useState } from "react";
import apiService, { BaseURL } from "../../../../service/Api";
import { tokens } from "../../../../theme";

const APPROVER_EMAILS = ["nigel@muzukuru.com", "joe@area46.co.zw", "veronica@area46.co.zw"];

const REASON_PRESETS = [
  {
    label: "Deposit paid",
    message: "Client has paid the required deposit.",
  },
  {
    label: "Contract signed",
    message: "Signed booking agreement/contract received from the client.",
  },
  {
    label: "Details verified",
    message:
      "All event details (date, venue, service) have been confirmed with the client.",
  },
  {
    label: "Verbal confirmation",
    message: "Client verbally confirmed via phone/WhatsApp.",
  },
  {
    label: "Balance cleared",
    message: "Outstanding balance has been paid in full.",
  },
  {
    label: "Availability confirmed",
    message: "Venue and date availability confirmed with no conflicts.",
  },
];

export interface StatusChangeApprover {
  id: number;
  name: string;
  email: string;
  avatarUrl?: string | null;
}

interface UsersResponse {
  results: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    profile_picture_url?: string | null;
  }[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  bookingLabel?: string;
  onSubmit: (approverIds: number[], reason: string) => Promise<void> | void;
}

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const getMediaUrl = (path?: string | null) => {
  if (!path) return undefined;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const cleanBaseUrl = BaseURL.replace(/\/$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${cleanBaseUrl}${cleanPath}`;
};

export default function RequestStatusChangeModal({
  open,
  onClose,
  bookingLabel,
  onSubmit,
}: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [approvers, setApprovers] = useState<StatusChangeApprover[]>([]);
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [selectedApproverIds, setSelectedApproverIds] = useState<number[]>([]);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedApproverIds([]);
      setReason("");
      return;
    }

    const fetchApprovers = async () => {
      try {
        setLoadingApprovers(true);
        const response = await apiService.get<UsersResponse>(
          "/api/authentication/users/",
          { role: "admin", page_size: 100 },
        );
        const matches = (response.data.results || [])
          .filter((u) => APPROVER_EMAILS.includes(u.email?.toLowerCase()))
          .map((u) => ({
            id: u.id,
            name: `${u.first_name} ${u.last_name}`.trim() || u.email,
            email: u.email,
            avatarUrl: u.profile_picture_url,
          }));
        setApprovers(matches);
      } catch {
        setApprovers([]);
      } finally {
        setLoadingApprovers(false);
      }
    };
    fetchApprovers();
  }, [open]);

  const toggleApprover = (id: number) =>
    setSelectedApproverIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );

  const canSubmit = selectedApproverIds.length > 0 && reason.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(selectedApproverIds, reason.trim());
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, maxWidth: 480, p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: 18, pb: 2 }}>
        Request Status Change
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 1 }}
        >
          Request approval from (select one or more)
        </Typography>

        {loadingApprovers ? (
          <CircularProgress size={22} sx={{ mb: 2 }} />
        ) : approvers.length === 0 ? (
          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
              mb: 2,
              p: 1.5,
              bgcolor: colors.primary[800],
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            No admin approvers found.
          </Typography>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 20,
            }}
          >
            {approvers.map((approver) => {
              const isSelected = selectedApproverIds.includes(approver.id);
              return (
                <Chip
                  key={approver.id}
                  clickable
                  onClick={() => toggleApprover(approver.id)}
                  avatar={
                    <Avatar src={getMediaUrl(approver.avatarUrl)}>
                      {getInitials(approver.name)}
                    </Avatar>
                  }
                  label={approver.name}
                  variant={isSelected ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    py: 2.5,
                    bgcolor: isSelected ? "#724E41" : "transparent",
                    color: isSelected ? "#fff" : undefined,
                    borderColor: "#724E41",
                    "& .MuiChip-avatar": {
                      color: isSelected ? "#724E41" : undefined,
                      bgcolor: isSelected ? "#fff" : undefined,
                    },
                  }}
                />
              );
            })}
          </div>
        )}

        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 1 }}
        >
          Reason (select or write your own)
        </Typography>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 12,
          }}
        >
          {REASON_PRESETS.map((preset) => (
            <Chip
              key={preset.label}
              clickable
              size="small"
              onClick={() => setReason(preset.message)}
              label={preset.label}
              variant={reason === preset.message ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                fontSize: 12,
                bgcolor: reason === preset.message ? "#724E41" : "transparent",
                color: reason === preset.message ? "#fff" : undefined,
                borderColor: "#724E41",
              }}
            />
          ))}
        </div>

        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder={`Why should ${
            bookingLabel ? `the booking for ${bookingLabel}` : "this booking"
          } be confirmed?`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: colors.primary[800],
              fontSize: 13,
              lineHeight: 1.6,
            },
          }}
        />

        <Button
          variant="contained"
          fullWidth
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
          startIcon={<HowToReg />}
          sx={{
            bgcolor: "#724E41",
            color: "white",
            textTransform: "none",
            borderRadius: 2,
            py: 1.2,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: "none",
            "&:hover": { bgcolor: "#5d3e33", boxShadow: "none" },
          }}
        >
          {submitting ? "Sending…" : "Send Request"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
